import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAnnouncements, createAnnouncement, uploadAttachments, createPoll } from '../api/apiClient';
import AnnouncementCard from './AnnouncementCard';
import RichTextInput from './RichTextInput';
import socket from '../socket';
import './HomeAnnouncements.css';

const HomeAnnouncements = ({ currentUser, isAdmin }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [showForm,      setShowForm]      = useState(false);
  const [formData,      setFormData]      = useState({ title: '' });
  const [contentData,   setContentData]   = useState({ text: '', mentions: [] });
  const [pendingFiles,  setPendingFiles]  = useState([]);
  const [fileInputKey,  setFileInputKey]  = useState(0);
  const [pollEnabled,   setPollEnabled]   = useState(false);
  const [pollQuestion,  setPollQuestion]  = useState('');
  const [pollOptions,   setPollOptions]   = useState(['', '']);
  const contentInputRef = useRef(null);
  const fileInputRef    = useRef(null);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const res = await getAnnouncements(currentUser?.id);
      setAnnouncements(res.data);
    } catch {}
    setLoading(false);
  }, [currentUser?.id]);

  useEffect(() => {
    fetchData();
    socket.on('announcements:changed', fetchData);
    return () => socket.off('announcements:changed', fetchData);
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !contentData.text) { alert('Please fill in all fields'); return; }
    try {
      const res = await createAnnouncement({
        title: formData.title, content: contentData.text,
        mentions: contentData.mentions, author_id: currentUser.id, is_admin: isAdmin,
      });
      if (pendingFiles.length > 0) {
        const fd = new FormData();
        pendingFiles.forEach(f => fd.append('files', f));
        await uploadAttachments(res.data.id, fd);
      }
      if (pollEnabled && pollQuestion.trim() && pollOptions.filter(o => o.trim()).length >= 2) {
        await createPoll({
          announcement_id: res.data.id,
          question: pollQuestion.trim(),
          options: pollOptions.filter(o => o.trim()),
        });
      }
      setFormData({ title: '' }); setContentData({ text: '', mentions: [] });
      contentInputRef.current?.clear(); setPendingFiles([]);
      setPollEnabled(false); setPollQuestion(''); setPollOptions(['', '']);
      setShowForm(false); fetchData();
      if (res.data.status === 'pending') {
        alert("Your announcement has been submitted for admin approval. You'll be notified once it's reviewed.");
      }
    } catch { alert('Error creating announcement'); }
  };

  const top = announcements.slice(0, 9);
  const featured = top.slice(0, 5);   // 5 full-length
  const secondary = top.slice(5, 9);  // up to 4 half-length (two rows of two)

  return (
    <div className="hap-wrap">
      <div className="hap-header">
        <h2 className="hap-heading">Announcements</h2>
        <div className="hap-header-actions">
          {isAdmin && currentUser?.id && (
            <button className="hap-post-btn" onClick={() => setShowForm(v => !v)}>
              {showForm ? '✕ Cancel' : '+ Post'}
            </button>
          )}
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="hap-form">
          <input
            type="text" placeholder="Announcement Title" value={formData.title}
            onChange={e => setFormData({ ...formData, title: e.target.value })}
            className="form-input"
          />
          <RichTextInput
            ref={contentInputRef} placeholder="What's your announcement?"
            className="rich-input--form"
            showToolbar
            htmlMode
            onChangeValue={(html, mentions) => setContentData({ text: html, mentions })}
          />
          <input key={fileInputKey} id="home-announce-file-input" ref={fileInputRef} type="file" multiple hidden onChange={e => {
            setPendingFiles(prev => [...prev, ...Array.from(e.target.files)]);
            setFileInputKey(k => k + 1);
          }} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.txt" />
          {pendingFiles.length > 0 && (
            <ul className="pending-files">
              {pendingFiles.map((f, i) => (
                <li key={i}><span>{f.name}</span>
                  <button type="button" onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))}>✕</button>
                </li>
              ))}
            </ul>
          )}
          {pollEnabled && (
            <div className="poll-builder">
              <input type="text" className="poll-question-input" placeholder="Ask a question…"
                value={pollQuestion} onChange={e => setPollQuestion(e.target.value)} maxLength={200} />
              <div className="poll-options-list">
                {pollOptions.map((opt, i) => (
                  <div key={i} className="poll-option-input-row">
                    <input type="text" className="poll-option-input" placeholder={`Option ${i + 1}`}
                      value={opt} onChange={e => { const n = [...pollOptions]; n[i] = e.target.value; setPollOptions(n); }} maxLength={100} />
                    {pollOptions.length > 2 && (
                      <button type="button" className="poll-remove-option"
                        onClick={() => setPollOptions(pollOptions.filter((_, j) => j !== i))}>✕</button>
                    )}
                  </div>
                ))}
              </div>
              {pollOptions.length < 4 && (
                <button type="button" className="poll-add-option-btn"
                  onClick={() => setPollOptions([...pollOptions, ''])}>+ Add option</button>
              )}
            </div>
          )}
          <div className="form-actions-row">
            <label htmlFor="home-announce-file-input" className="split-btn split-btn--sm" style={{ cursor: 'pointer' }}>
              <span className="split-btn-main">📎 Attach</span>
            </label>
            <button type="button"
              className={`split-btn split-btn--sm${pollEnabled ? ' split-btn--active' : ''}`}
              onClick={() => { setPollEnabled(v => !v); setPollQuestion(''); setPollOptions(['', '']); }}>
              <span className="split-btn-main">📊 {pollEnabled ? 'Remove Poll' : 'Add Poll'}</span>
            </button>
            <button type="submit" className="split-btn split-btn--sm split-btn--action">
              <span className="split-btn-main">✓ Post</span>
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="hap-loading">Loading…</p>
      ) : top.length === 0 ? (
        <p className="hap-loading">No announcements yet.</p>
      ) : (
        <div className="hap-grid">
          {featured.map(a => (
            <div key={a.id} className="hap-featured">
              <AnnouncementCard
                announcement={a}
                currentUser={currentUser}
                isAdmin={isAdmin}
                hideComments
                noCharLimit
                clampTextBy={0}
                onCommentAdded={fetchData}
                onAnnouncementDeleted={fetchData}
              />
            </div>
          ))}
          {secondary.length > 0 && (
            <div className="hap-secondary-row">
              {secondary.map(a => (
                <div key={a.id} className="hap-secondary">
                  <AnnouncementCard
                    announcement={a}
                    currentUser={currentUser}
                    isAdmin={isAdmin}
                    hideComments
                    onCommentAdded={fetchData}
                    onAnnouncementDeleted={fetchData}
                  />
                </div>
              ))}
            </div>
          )}
          <div className="hap-view-all-wrap">
            <button className="hap-view-all-pill" onClick={() => navigate('/announcements')}>
              View all announcements
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeAnnouncements;
